import type { BoxProps } from "@mui/material/Box"

import { useId, forwardRef } from "react"
import { useAuthStore } from "@/src/store"
import { colorByName } from "@/src/theme/core/components/avatar"

import Box from "@mui/material/Box"
import { Avatar } from "@mui/material"
import NoSsr from "@mui/material/NoSsr"
import { useTheme } from "@mui/material/styles"

import { logoClasses } from "./classes"

// ----------------------------------------------------------------------

export type LogoProps = BoxProps & {
    href?: string
    disableLink?: boolean
    disableBg?: boolean
}

export const Logo = forwardRef<HTMLDivElement, LogoProps>(
    ({ width = 39, height = 39, disableLink = false, disableBg = false, className, href = "/", sx, ...other }, ref) => {
        const user = useAuthStore((state) => state.user)
        const theme = useTheme()

        const gradientId = useId()

        const PRIMARY_LIGHT = theme.vars.palette.primary.light

        const PRIMARY_MAIN = theme.vars.palette.primary.main

        const PRIMARY_DARK = theme.vars.palette.primary.dark
        const { color, bgColor } = colorByName(user?.activeWorkspace?.name ?? "")

        // Don't render logo on login page when there's no user or workspace
        if (!user || !user.activeWorkspace) {
            return null
        }

        const logo = user?.activeWorkspace?.logoUrl ? (
            <Box
                alt="logo"
                component="img"
                src={user.activeWorkspace.logoUrl ?? undefined}
                width={width}
                height={height}
                sx={{ objectFit: "contain" }}
            />
        ) : (
            <Avatar
                src={user?.activeWorkspace?.logoUrl}
                sx={{
                    width: "56px",
                    height: "56px",
                    color: "#ffffff",
                    fontSize: "24px",
                    cursor: "pointer",
                    backgroundColor: bgColor,
                }}
                color={color}
            >
                {user?.activeWorkspace?.name?.charAt(0)}
            </Avatar>
        )

        return (
            <NoSsr
                fallback={
                    <Box
                        width={width}
                        height={height}
                        className={logoClasses.root.concat(className ? ` ${className}` : "")}
                        sx={{ flexShrink: 0, display: "inline-flex", verticalAlign: "middle", ...sx }}
                    />
                }
            >
                <Box
                    ref={ref}
                    href={href}
                    width={width}
                    height={height}
                    className={logoClasses.root.concat(className ? ` ${className}` : "")}
                    aria-label="logo"
                    sx={{
                        textDecoration: "none",
                        flexShrink: 0,
                        display: "inline-flex",
                        verticalAlign: "middle",
                        ...(disableLink && { pointerEvents: "none" }),
                        ...sx,
                    }}
                    {...other}
                >
                    {logo}
                </Box>
            </NoSsr>
        )
    }
)
